import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useAuthStore } from "./store/authStore";
import { tryHydrateSession } from "./api/axiosInstance";
import { loginRequest } from "./api/authApi";
import { ProtectedRoute } from "./components/Auth/ProtectedRoute";
import { LoginPage } from "./pages/LoginPage";
import { PlansPage } from "./pages/PlansPage";
import { ViewerPage } from "./pages/ViewerPage";
import { HistoryPage } from "./pages/HistoryPage";
import { AdminPage } from "./pages/AdminPage";
import { OfflineBanner } from "./components/UI/OfflineBanner";

/**
 * Hidrata la sesion al arrancar: si hay cookie HttpOnly, /auth/refresh
 * devuelve un access token y lo guardamos en memoria.
 * Para recuperar el user (sin guardarlo en DB aqui) re-ejecutamos login NO
 * es posible sin password: por ello el backend deberia incluir user en
 * /auth/refresh si queremos hidratacion completa. Por ahora, si hay token
 * valido pero no user, decodificamos el JWT del lado cliente.
 */
function parseJwt<T>(token: string): T | null {
  try {
    const payload = token.split(".")[1];
    if (!payload) return null;
    // base64url -> base64
    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const json = atob(base64);
    return JSON.parse(json) as T;
  } catch {
    return null;
  }
}

function useBootstrap() {
  const { setUser, setHydrating, setAccessToken } = useAuthStore();

  useEffect(() => {
    (async () => {
      const ok = await tryHydrateSession();
      if (ok) {
        const token = useAuthStore.getState().accessToken;
        if (token) {
          const payload = parseJwt<{ sub: string; email: string; role: "ADMIN" | "TECHNICIAN" | "VIEWER" }>(token);
          if (payload) {
            setUser({ id: payload.sub, email: payload.email, role: payload.role });
          } else {
            setAccessToken(null);
          }
        }
      }
      setHydrating(false);
    })();
  }, [setUser, setHydrating, setAccessToken]);
}

export function App() {
  useBootstrap();
  // Exponer loginRequest al window en dev facilita pruebas manuales
  if (import.meta.env.DEV) {
    (window as unknown as { __login?: typeof loginRequest }).__login = loginRequest;
  }

  return (
    <BrowserRouter>
      <OfflineBanner />
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route
          path="/plans"
          element={
            <ProtectedRoute>
              <PlansPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/plans/:id"
          element={
            <ProtectedRoute>
              <ViewerPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/history"
          element={
            <ProtectedRoute>
              <HistoryPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin"
          element={
            <ProtectedRoute requireRoles={["ADMIN"]}>
              <AdminPage />
            </ProtectedRoute>
          }
        />
        <Route path="*" element={<Navigate to="/plans" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
